import React, { useState } from 'react';
import Appearance from 'styles/Appearance.js';
import Utils from 'files/Utils.js';

// ---------------------------------------------------------------------------
// Demo video — PLUG IN LATER
// ---------------------------------------------------------------------------
// This section recreates the in-home rep's live demo for cold web visitors.
// It's intentionally the highest-value asset on the page, so it gets the
// hero slot of the "How It Works" section.
//
// To go live, set `videoUrl` to either:
//   • a self-hosted file placed in /public/images (e.g. '/images/demo.mp4'), or
//   • a hosted embed URL (YouTube/Vimeo/Wistia) — set `embed: true` for those.
// Optionally set `posterUrl` to a still frame shown before playback.
// While `videoUrl` is null, a branded placeholder renders in its place.
const demoVideo = {
    videoUrl: '/images/water-sensors-video-us.mp4',
    embed: false,
    posterUrl: '/images/water-sensors-video-us.jpg',
    caption: 'Learn more about the water sensors and their home safe network',
};

const HowItWorks = () => {

    const [playing, setPlaying] = useState(false);

    // Maps to the deck: Detect (water sensor) → Alert (Smart Hub + app) → Act (you know where)
    const steps = [
        {
            icon: 'fas fa-tint',
            title: 'Detect',
            copy: 'Place wireless sensors anywhere water runs — under sinks, by the water heater, behind the fridge. 23k gold contacts catch the first sign of a leak or freeze.',
        },
        {
            icon: 'fas fa-broadcast-tower',
            title: 'Alert',
            copy: 'The Smart Hub instantly pushes a text, email, and app alert — to you and up to 8 emergency contacts. No monthly monitoring fees.',
        },
        {
            icon: 'fas fa-map-marker-alt',
            title: 'Act',
            copy: 'You know exactly where the problem is — the kitchen sink, the laundry room — so you can stop it before it becomes damage, mold, or a ruined heirloom.',
        },
    ];

    const renderVideo = () => {

        // Live video, once plugged in
        if(demoVideo.videoUrl) {
            if(demoVideo.embed) {
                return (
                    <iframe
                        src={demoVideo.videoUrl}
                        title={'Product demo'}
                        allow={'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'}
                        allowFullScreen={true}
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            border: 'none',
                        }} />
                );
            }
            return (
                <video
                    controls={true}
                    autoPlay={playing}
                    poster={demoVideo.posterUrl}
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        backgroundColor: Appearance.colors.dark(),
                    }}>
                    <source src={demoVideo.videoUrl} type={'video/mp4'} />
                </video>
            );
        }

        // Placeholder until the video is plugged in
        return (
            <div
                onClick={() => setPlaying(true)}
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 20,
                    cursor: 'pointer',
                    background: `linear-gradient(135deg, ${Appearance.colors.tertiary()} 0%, ${Appearance.colors.dark()} 100%)`,
                }}>

                {/* Play button */}
                <div style={{
                    width: 84,
                    height: 84,
                    borderRadius: '50%',
                    backgroundColor: Utils.hexToRGBA(Appearance.colors.white, 0.15),
                    border: `2px solid ${Utils.hexToRGBA(Appearance.colors.white, 0.6)}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}>
                    <i className={'fas fa-play'} style={{
                        fontSize: 30,
                        color: Appearance.colors.white,
                        marginLeft: 5,
                    }} />
                </div>

                <span style={Appearance.textStyles.subHeading({
                    color: Appearance.colors.white,
                    textAlign: 'center',
                    padding: '0 24px',
                })}>
                    {'Demo video coming soon'}
                </span>
                <span style={Appearance.textStyles.caption({
                    color: Utils.hexToRGBA(Appearance.colors.white, 0.75),
                    textAlign: 'center',
                    padding: '0 24px',
                    maxWidth: 420,
                })}>
                    {demoVideo.caption}
                </span>
            </div>
        );
    };

    return (
        <section id={'how-it-works'} style={{
            width: '100%',
            backgroundColor: Appearance.colors.white,
        }}>
            <div style={{
                maxWidth: Appearance.layout.maxWidth,
                margin: '0 auto',
                padding: Appearance.layout.sectionPadding(),
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
            }}>

                {/* Heading */}
                <span style={{ ...Appearance.textStyles.label(), marginBottom: 14, textAlign: 'center' }}>
                    {'How It Works'}
                </span>
                <h2 style={{ ...Appearance.textStyles.sectionHeading(), marginBottom: 16 }}>
                    {'Know in seconds, from anywhere'}
                </h2>
                <p style={{ ...Appearance.textStyles.sectionSubheading(), marginBottom: Utils.isMobile() ? 36 : 48 }}>
                    {'A wireless network can watch every spot water runs in your home, and inform you when  something is wrong.'}
                </p>

                {/* Demo video (16:9) */}
                <div style={{
                    width: '100%',
                    position: 'relative',
                    paddingTop: '56.25%',
                    borderRadius: 16,
                    overflow: 'hidden',
                    border: `1px solid ${Appearance.colors.border()}`,
                    boxShadow: Appearance.boxShadow.card,
                    marginBottom: Utils.isMobile() ? 40 : 56,
                }}>
                    {renderVideo()}
                </div>

                {/* Detect → Alert → Act — three bounded steps, matching the card system */}
                <div style={{
                    width: '100%',
                    display: 'grid',
                    gridTemplateColumns: Utils.isMobile() ? '1fr' : 'repeat(3, 1fr)',
                    gap: Utils.isMobile() ? 20 : 24,
                }}>
                    {steps.map((step, index) => (
                        <div key={index} style={{
                            ...Appearance.styles.card(),
                            boxShadow: Appearance.boxShadow.card,
                            padding: Utils.isMobile() ? 24 : 28,
                            textAlign: 'left',
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                                <i className={step.icon} style={{ fontSize: 19, color: Appearance.colors.primary(), flexShrink: 0 }} />
                                <h3 style={{ ...Appearance.textStyles.subHeading({ fontSize: 18 }), margin: 0 }}>
                                    {step.title}
                                </h3>
                            </div>
                            <p style={Appearance.textStyles.body({ fontSize: 14 })}>
                                {step.copy}
                            </p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default HowItWorks;
